import React, { useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import ModalBodyLayout from '../partials/MainBodyLayout';
import { Button, Typography } from '../../../../../components';
import { useLedgerFilteredCurrencies } from '../../../../../utilities/sharedHooks/ledgerWallets';
import { SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY } from '../../../../../utilities/sharedQueries';
import CurrencyIcon from '../../../../../components/CurrencyIcon';
import { currencies, currenciesDefault } from '../../../../../utilities/constants';
import { copyToClipboard } from '../../../../../utilities/helpers';
import { orderCardFlowScreens } from '../../../constants';

const TopUpReceiveExternally = () => {
  const { t: translate } = useTranslation();
  const { state, openScreen } = useContext(OrderCardContext);

  const screenOptions = state?.screenOptions;

  const collateralWallet = screenOptions ? screenOptions.wallet : null;

  const currency = collateralWallet?.coin.toUpperCase();

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const walletAddressObj = useMemo(() => {
    const addressArr = collateralWallet?.address?.split('?dt=');

    const [address, memo] = addressArr;

    return { address, memo };
  }, [collateralWallet]);

  const currencyDisplayWithToken = useMemo(() => {
    let displayCurrency = currency?.toUpperCase();
    if (supportedCurrencies) {
      if (
        supportedCurrencies[currency?.toLowerCase()] &&
        supportedCurrencies[currency?.toLowerCase()]?.is_erc20_token
      ) {
        displayCurrency = `ERC20 ${currency.toUpperCase()}`;
      }
    }
    return displayCurrency;
  }, [supportedCurrencies, currency]);

  const currencyDisplayWithFullName = useMemo(() => {
    let displayCurrency = currenciesDefault[currency.toUpperCase()]?.fullName;
    if (supportedCurrencies) {
      if (
        supportedCurrencies[currency?.toLowerCase()] &&
        supportedCurrencies[currency?.toLowerCase()]?.is_erc20_token
      ) {
        displayCurrency = 'Ethereum';
      }
    }
    return displayCurrency;
  }, [supportedCurrencies, currency]);

  const renderTitle = () => (
    <>
      {/* mobile */}
      <Typography variant="largeBold" className="d-block d-md-none text-dark me-auto">
        <CurrencyIcon
          currencyCode={currency}
          className="me-2"
          style={{
            width: '2.2rem',
            height: '2.2rem',
            ...(currency?.toUpperCase() === currencies.XRP.icon
              ? {
                  backgroundColor: 'white',
                  padding: '1px',
                  borderRadius: '20px',
                }
              : {}),
          }}
        />
        {translate('deposit_wallet_receive').replace('%s', currency.toUpperCase())}
      </Typography>

      {/* browser */}

      <Typography variant="xLargeBold" className="d-none d-md-block text-dark me-auto">
        <CurrencyIcon
          currencyCode={currency}
          className="me-2"
          style={{
            width: '2.2rem',
            height: '2.2rem',
            ...(currency?.toUpperCase() === currencies.XRP.icon
              ? {
                  backgroundColor: 'white',
                  padding: '1px',
                  borderRadius: '20px',
                }
              : {}),
          }}
        />
        {translate('deposit_wallet_receive').replace('%s', currency.toUpperCase())}
      </Typography>
    </>
  );

  const renderFooter = () => (
    <div className="btn-container">
      <Button
        variant="primary"
        className="w-100"
        onClick={() => copyToClipboard(walletAddressObj?.address)}>
        {translate('copy_to_clipboard_btn_label')}
      </Button>
    </div>
  );

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() => openScreen(orderCardFlowScreens.LEDGER_DEPOSIT, { ...screenOptions })}
      title={renderTitle()}>
      <div className="mt-4 w-100">
        <div className="d-flex flex-row justify-content-between align-items-center">
          <div className="d-flex flex-column me-2">
            <Typography as="p" variant="small" className="text-shadow mt-3">
              {translate('address_to_receive_crypto_label').replace('__CURRENCY__', currency)}
            </Typography>

            {/* mobile */}
            <Typography
              as="p"
              variant="base"
              className="d-block d-md-none text-dark"
              style={{
                wordBreak: 'break-word',
              }}>
              {walletAddressObj?.address}
            </Typography>

            {/*  browser */}
            <Typography
              as="p"
              variant="base"
              className="d-none d-md-block text-dark"
              style={{
                wordBreak: 'break-word',
                width: walletAddressObj?.memo ? '' : '25rem',
              }}>
              {walletAddressObj?.address}
            </Typography>
          </div>
          {walletAddressObj?.memo && (
            <div className="align-self-end px-1 pt-1 bg-copy rounded-max">
              <i
                onKeyDown={null}
                tabIndex={-1}
                role="button"
                aria-label="Copy"
                className="icon-copy-other text-primary text-f4 cursor-pointer"
                onClick={() => copyToClipboard(walletAddressObj?.address)}
              />
            </div>
          )}
        </div>
      </div>

      {walletAddressObj?.memo && (
        <div className="mt-2 w-100">
          <div className="d-flex flex-row justify-content-between  align-items-center">
            <div className="d-flex flex-column">
              <Typography as="p" variant="small" className="text-shadow upper-case mt-3">
                {translate('receive_memo_field_label')}
              </Typography>
              <Typography as="p" variant="base" className="text-dark">
                {walletAddressObj?.memo}
              </Typography>
            </div>
            <div className="align-self-end px-1 pt-1 bg-copy rounded-max">
              <i
                onKeyDown={null}
                tabIndex={-1}
                role="button"
                aria-label="Copy"
                className="icon-copy-other text-primary text-f4 cursor-pointer"
                onClick={() => copyToClipboard(walletAddressObj?.memo)}
              />
            </div>
          </div>
        </div>
      )}
      <div
        className={`d-flex flex-column justify-content-around gap-5 align-items-center ${
          walletAddressObj?.memo ? 'mt-4' : 'mt-5'
        }`}>
        <div className="bg-white p-2">
          <QRCode value={walletAddressObj?.address} size={200} level="M" bgColor="#ffffff" />
        </div>

        <div className="alert alert-info" role="alert">
          <i className="icon icon-info-icon" />
          {walletAddressObj?.memo ? (
            <span className="content">
              <p>
                <Trans i18nKey="receive_with_memo_note" components={[<b key="content-1" />]} />
              </p>
              <p>
                <Trans
                  i18nKey="receive_with_memo_note_1"
                  components={[<b key="content-1" />, <b key="content-2" />]}
                />
              </p>
              <p>
                <Trans i18nKey="receive_with_memo_note_2" />
              </p>
            </span>
          ) : (
            <span className="content">
              <Trans
                i18nKey="receive_with_crypto_note"
                values={{
                  currency: currencyDisplayWithToken,
                  full_name: currencyDisplayWithFullName,
                }}
                components={[<b key="content-1" />, <b key="content-2" />]}
              />
            </span>
          )}
        </div>
      </div>
      {renderFooter()}
    </ModalBodyLayout>
  );
};

export default TopUpReceiveExternally;
